/**
 * Predicate kernel shared between the Collection-query resolver and the Grid
 * view filter engine. Pure JS — no Airtable-specific shapes leak in here.
 *
 * What lives here:
 *   - The canonical operator table (OPERATORS_BY_TYPE), grouped by field type.
 *   - Operator aliases (eq → =, not_contains → does_not_contain, etc.) so the
 *     resolver's stored JSON shape and Grid's filter-chip state both keep
 *     working through one canonical comparator.
 *   - readNodeField — the "read a field by name, regardless of which bucket
 *     it lives in" helper that fixes the customFields-bypass bug in the
 *     resolver. Matches the precedence used everywhere else in the app:
 *     customFields wins, then _rawFields, then a camelCase top-level fallback.
 *   - resolveFilterValue — substitutes $today / $now tokens. Future $context
 *     substitution will land here.
 *   - applyPredicate — the per-operator comparator. Already-extracted values
 *     in, boolean out. fieldType is optional; without it the comparator falls
 *     back to type-agnostic behavior compatible with the resolver's current
 *     stored queries.
 *
 * What does NOT live here:
 *   - Grid's column dispatch. testFilter() in GridCanvas/filters.js keeps the
 *     row.accessor/stringify/id-vs-label branches because those are
 *     column-aware (need ctx.displayTypes / ctx.nodeMap) and don't belong in
 *     a generic kernel.
 *   - Reserved but unimplemented: linked_is, linked_is_not, linked_has_any,
 *     linked_has_none. The names are reserved; nothing implements them. They hit
 *     applyPredicate's `default:` branch, which FAILS OPEN and returns true — the
 *     resolver's deliberate historical behavior so a stray op name cannot empty a
 *     whole view. This comment claimed the opposite ("fail loudly ... rather than
 *     silently returning true") until 2026-09-08; it never did. Whether these four
 *     should be the exception that throws is a real question, and it dissolves when
 *     they are implemented — decide it then, not by leaving a wrong comment here.
 */

// flexDate is a dependency-free primitive (no imports of its own, so no cycle).
// Using it here rather than re-deriving the shapes gets the Feb-29 clamp and the
// day-in-month clamp for free — both matter once annual dates are projected.
import { isAnnual, projectToYear, toDate as flexDateToDate } from './flexDate.js';
import { createdDate, isCreatedField } from './createdDate.js';
import { collectionFlag, isCollectionField } from './collectionField.js';
import { priceBandWord, isPriceBandField } from './priceBandField.js';
import { isApprovalField, approvalWord } from './approvalField.js';
import { isSpaceField, owningSpaceName } from '../data/owningSpace.js';

// ─── Operator tables ─────────────────────────────────────────────────────

const TEXT_OPS = [
  { key: 'contains',         label: 'contains' },
  { key: 'does_not_contain', label: 'does not contain' },
  { key: 'is',               label: 'is' },
  { key: 'is_not',           label: 'is not' },
  { key: 'is_empty',         label: 'is empty' },
  { key: 'is_not_empty',     label: 'is not empty' },
];

const NUMBER_OPS = [
  { key: 'eq',           label: '=' },
  { key: 'neq',          label: '≠' },
  { key: 'gt',           label: '>' },
  { key: 'lt',           label: '<' },
  { key: 'gte',          label: '≥' },
  { key: 'lte',          label: '≤' },
  { key: 'is_empty',     label: 'is empty' },
  { key: 'is_not_empty', label: 'is not empty' },
];

// has_any / has_none on a single select read as "is any of" / "is none of" — the value holds
// one option, so "has" would be the wrong verb. Same keys the multi-select uses, and the same
// labels the server's result-set rules already give them (api/_resultSetRules.js).
const SELECT_OPS = [
  { key: 'is',           label: 'is' },
  { key: 'is_not',       label: 'is not' },
  { key: 'has_any',      label: 'is any of' },
  { key: 'has_none',     label: 'is none of' },
  { key: 'is_empty',     label: 'is empty' },
  { key: 'is_not_empty', label: 'is not empty' },
];

const MULTI_SELECT_OPS = [
  { key: 'has_any',      label: 'has any of' },
  { key: 'has_none',     label: 'has none of' },
  { key: 'is_empty',     label: 'is empty' },
  { key: 'is_not_empty', label: 'is not empty' },
];

// `is_before` stays first so getDefaultOperator() keeps returning it for a
// freshly-added date filter. `is_on` reads better as a default and is what
// Airtable defaults to; flip the order when the relative/exact value control
// lands, not before — the default operator is visible in every existing view.
//
// is_in_the_past / is_in_the_future take no value: they compare against "now"
// (ctx.now, else the wall clock). For a day-precision field they leave a gap —
// today is neither past nor future — which is correct for an expiration ("still
// live all day"). "Not expired yet" is therefore is_on_or_after + $today, not
// is_in_the_future.
// is_within_next / is_within_past carry BOTH bounds of a window in one
// condition, with a duration value ("7d", "2w", "48h") rather than a date. One
// operator instead of a pair matters beyond brevity: a chip can only write a
// condition set, so a window built from two comparators reads in the composer
// as two cryptic bounds, and any UI that shows "the operator" has nothing
// single to show.
const DATE_OPS = [
  { key: 'is_before',         label: 'is before' },
  { key: 'is_after',          label: 'is after' },
  { key: 'is_on',             label: 'is on' },
  { key: 'is_on_or_before',   label: 'is on or before' },
  { key: 'is_on_or_after',    label: 'is on or after' },
  { key: 'is_within_next',    label: 'is within the next' },
  { key: 'is_within_past',    label: 'is within the past' },
  { key: 'is_in_the_past',    label: 'is in the past' },
  { key: 'is_in_the_future',  label: 'is in the future' },
  { key: 'is_empty',          label: 'is empty' },
  { key: 'is_not_empty',      label: 'is not empty' },
];

// The date-comparison ops — the set that routes through coerceToStamp /
// compareStamps rather than string or numeric comparison. Excludes
// is_empty / is_not_empty, which are type-agnostic and handled earlier by
// every caller. Exported so the Grid engine can dispatch on it instead of
// re-listing the operator names.
const DATE_COMPARISON_OPS = new Set([
  'is_on', 'is_before', 'is_after', 'is_on_or_before', 'is_on_or_after',
  'is_within_next', 'is_within_past',
  'is_in_the_past', 'is_in_the_future',
]);

export function isDateOperator(op) {
  return DATE_COMPARISON_OPS.has(canonicalizeOp(op));
}

// The window ops, whose value is a duration ("7d") rather than a date. Callers
// need this to know which value editor to render and which default to seed:
// handing "$today" to is_within_next produces an unparseable condition.
const DURATION_OPS = new Set(['is_within_next', 'is_within_past']);

export function isDurationOperator(op) {
  return DURATION_OPS.has(canonicalizeOp(op));
}

const CHECKBOX_OPS = [
  { key: 'is_checked',     label: 'is checked' },
  { key: 'is_not_checked', label: 'is not checked' },
];

export const OPERATORS_BY_TYPE = {
  singleLineText:      TEXT_OPS,
  multilineText:        TEXT_OPS,
  richText:             TEXT_OPS,
  url:                  TEXT_OPS,
  email:                TEXT_OPS,
  phoneNumber:          TEXT_OPS,
  number:               NUMBER_OPS,
  currency:             NUMBER_OPS,
  percent:              NUMBER_OPS,
  integer:              NUMBER_OPS,
  decimal:              NUMBER_OPS,
  count:                NUMBER_OPS,
  singleSelect:         SELECT_OPS,
  displayType:          SELECT_OPS,
  multipleSelects:      MULTI_SELECT_OPS,
  multipleRecordLinks:  MULTI_SELECT_OPS,
  date:                 DATE_OPS,
  dateTime:             DATE_OPS,
  createdTime:          DATE_OPS,
  lastModifiedTime:     DATE_OPS,
  checkbox:             CHECKBOX_OPS,
  // A status field stores one option name per cell, exactly as a single select does. What makes it
  // its own type is the EDITOR's option model (colours, nesting, the Do again rule), not how a value
  // matches — so without this entry it fell through to TEXT_OPS, and the toolbar filter offered
  // "contains" and a box to type the option into.
  status:               SELECT_OPS,
};

const DEFAULT_OPS = TEXT_OPS;

export function operatorsForType(type) {
  return OPERATORS_BY_TYPE[type] || DEFAULT_OPS;
}

// Every operator any type offers, by key — so a filter row can still NAME an operator its field's
// type no longer offers.
const OPERATOR_BY_KEY = new Map(Object.values(OPERATORS_BY_TYPE).flat().map((o) => [o.key, o]));

/**
 * The operators a filter row's dropdown offers: its type's set, plus the row's own stored operator
 * when that set does not include it.
 *
 * A filter outlives the type it was written against. Until `status` had an entry above, a status
 * field filtered as text, so saved filters exist reading "does not contain Completed". The
 * evaluator still honours them — it is not type-gated — but a <select> whose value is in none of
 * its options displays the FIRST one, so the row would calmly say "is" while filtering by "does not
 * contain". Keeping the stored operator in the list makes the row say what it does; choosing any
 * other operator drops it, since it is no longer the current one.
 *
 * @param {string} type      the field's filter type
 * @param {string} [current] the operator the filter has stored
 * @returns {Array<{key: string, label: string}>}
 */
export function operatorsForFilter(type, current) {
  const ops = operatorsForType(type);
  if (!current || ops.some((o) => o.key === current)) return ops;
  const known = OPERATOR_BY_KEY.get(canonicalizeOp(current));
  return [...ops, { key: current, label: known?.label || current }];
}

// Operators that need no value input (so callers can hide the value picker
// and applyPredicate can ignore the target argument).
const NO_VALUE_OPS = new Set([
  'is_empty', 'is_not_empty', 'is_checked', 'is_not_checked',
  // Relative-to-now date ops carry their comparison target in the operator
  // itself. Marking them value-less is what lets applyFilters' completeness
  // gate pass them through — it drops any filter whose value is empty, which
  // is why an unfilled "is before ___" row today counts as "1 Filter" in the
  // toolbar while filtering nothing.
  'is_in_the_past', 'is_in_the_future',
]);

export function operatorNeedsValue(op) {
  return !NO_VALUE_OPS.has(canonicalizeOp(op));
}

// ─── Aliases ──────────────────────────────────────────────────────────────

// Both stored-query shapes (resolver historical: =, !=, not_contains) and
// Grid filter-chip shapes (eq, neq) survive by canonicalizing here. New code
// should use whichever canonical name reads best for its surface — both work.
const OPERATOR_ALIASES = {
  eq:           '=',
  '==':         '=',
  neq:          '!=',
  not_contains: 'does_not_contain',
  // Reserved for Phase 2 — let them fall through to the unknown-op default
  // (returns true; the chip-builder UI is the gate, not the kernel).
  // linked_is, linked_is_not, linked_has_any, linked_has_none — not aliased
  // intentionally; they need real implementations before being exposed.
};

export function canonicalizeOp(op) {
  return OPERATOR_ALIASES[op] || op;
}

// ─── Dynamic value tokens ─────────────────────────────────────────────────

// The viewer-relative binding token — the `:` render-parameter namespace. Bound
// to the current account at read time; it's the variable behind "My Favorites"
// and every personal/dynamic cell collection (an admin authors one Source; each
// viewer resolves their own). Deliberately NOT `@current_user`: the `@` namespace
// is audiences (item viewers / editors / groups), so a `:` keeps the query
// parameter from colliding with the people who can view/edit a thing.
export const VIEWER_TOKEN = ':current_user';

// The viewer's own PROFILE ANSWER, as a comparison value: `:my_answer(<field_key>)`.
//
// The sibling token above answers "who is looking"; this one answers "what did they say". It is
// what makes a shared collection resolve differently per person without storing anything per
// person — "events in the topics I chose" is an ordinary field comparison whose right-hand side
// happens to be the viewer's answer to a profile question.
//
// Deliberately a VALUE token rather than a new filter source. A cell filter (applyCellFilter)
// asks whether the viewer interacted with the item being tested; this asks for a value stored on
// a DIFFERENT item — the viewer's own profile — and then compares it against the item's field.
// Keeping it in the value position means every existing operator works with it unchanged.
//
// Resolves to null when the viewer has not answered, which `has_any` already treats as "match
// nothing" (an empty target list). That is the intended safe default: someone who has not chosen
// any topics sees no topic matches, never every event in the space.
const MY_ANSWER_TOKEN_RE = /^:my_answer\(([a-zA-Z0-9_-]{1,64})\)$/;

/** The field key inside a `:my_answer(...)` token, or null when `value` isn't one. */
export function parseMyAnswerToken(value) {
  if (typeof value !== 'string') return null;
  const m = MY_ANSWER_TOKEN_RE.exec(value);
  return m ? m[1] : null;
}

/** Build the token for a field key — so call sites never hand-spell the syntax. */
export function makeMyAnswerToken(fieldKey) {
  return `:my_answer(${fieldKey})`;
}

/**
 * `$context_tree(<link type id>)` — the anchor item AND every item below it along one hierarchical
 * link type, as the comma-separated id list `has_any` already reads.
 *
 * Why a token and not a traversal Source: "events tagged Swing or any style under Swing" is a filter
 * on the EVENTS (their `Has Genre` links) whose target set is a subtree of the GENRES. A relationship
 * Source walks from the anchor and can only return what it reaches, so it would return the genres,
 * not the events; the filter kernel is where the events are, and it only needs the target set.
 *
 * The walk follows edges source → target, the way every hierarchical link type in the app is stored
 * (parent → child: Has Sub-Genre, Includes — see views/wiki/hierarchyResolver.js). It is cycle-safe,
 * and it needs `ctx.allEdges`: a surface that passes no edges gets the anchor alone, which is the
 * plain `$context` answer rather than nothing.
 */
const CONTEXT_TREE_TOKEN_RE = /^\$context_tree\(([a-zA-Z0-9_-]{1,64})\)$/;

/** The link type id a `$context_tree(...)` token walks, or null when `value` is not one. */
export function parseContextTreeToken(value) {
  if (typeof value !== 'string') return null;
  const m = CONTEXT_TREE_TOKEN_RE.exec(value);
  return m ? m[1] : null;
}

/** Build the token for a link type — so call sites never hand-spell the syntax. */
export function makeContextTreeToken(linkTypeId) {
  return `$context_tree(${linkTypeId})`;
}

/** `rootId` and everything reachable from it along `linkTypeId`, source → target, root first. */
export function subtreeIds(rootId, edges, linkTypeId) {
  const children = new Map();
  for (const e of edges || []) {
    if (e?.displayTypeId !== linkTypeId) continue;
    if (!children.has(e.source)) children.set(e.source, []);
    children.get(e.source).push(e.target);
  }
  const seen = new Set([rootId]);
  const queue = [rootId];
  while (queue.length) {
    for (const c of children.get(queue.shift()) || []) {
      if (!seen.has(c)) { seen.add(c); queue.push(c); }
    }
  }
  return [...seen];
}

/**
 * Substitute dynamic value tokens. Two sigils, two intents:
 *
 *   `$…` — value-substitution tokens (replace a literal comparison value):
 *     - $today    → YYYY-MM-DD in the VIEWER'S timezone. Deliberately local,
 *                   not UTC: at 20:00 in UTC-7 the UTC date is already
 *                   tomorrow, so a UTC $today meant "is after tomorrow" for
 *                   every western viewer for a third of the day. Day-only
 *                   strings are also parsed locally (see coerceToStamp), so
 *                   both sides of a day comparison share one anchor.
 *     - $today±Nd → N days before/after today, as a local YYYY-MM-DD ("$today+7d",
 *                   "$today-30d"). `w` counts weeks ("$today-2w"). One grammar
 *                   rather than a $yesterday / $tomorrow zoo — the UI supplies
 *                   the human label, so "Yesterday" is just $today-1d. Day
 *                   arithmetic is calendar-based, so a DST transition can't
 *                   land the result on the wrong day.
 *     - $now      → full ISO timestamp
 *     - $context_tree(<link type id>) → the anchor item plus everything under it
 *                   along that hierarchical link type, comma-separated for
 *                   has_any (see parseContextTreeToken). Null with no anchor.
 *     - $context  → the active view's anchor item id (Phase 3). Resolves to
 *                   null when no anchor is available; null then short-circuits
 *                   most predicate ops to false so a "where Track is $context"
 *                   query against a non-contextual surface matches nothing
 *                   rather than silently matching everything.
 *
 *   `:…` — render-context binding tokens (bind the query to who/where it runs):
 *     - :current_user → the viewing account's id (ctx.currentUserId), resolved
 *                       at read time. Null when no viewer is known (which then
 *                       short-circuits comparisons to false — the safe default).
 *     - :my_answer(<field_key>) → the viewing account's own answer to a profile
 *                       field (ctx.viewerAnswers[field_key]). Null when the slice
 *                       wasn't loaded or the question is unanswered, which
 *                       `has_any` reads as an empty target and so matches
 *                       nothing — never everything.
 *
 * The optional ctx parameter carries contextItemId ($context) and currentUserId
 * (:current_user). Callers that don't pass ctx still get $today / $now
 * substitution (back-compat with pre-Phase-3 call sites).
 *
 * @param {*} value  The raw filter value (any shape, but typically a string).
 * @param {Object} [ctx]
 * @param {string|null} [ctx.contextItemId]  The active view's anchor item id.
 * @param {string|null} [ctx.currentUserId]  The viewing account's id.
 * @param {Date|string|number} [ctx.now]  The instant "now" resolves to. Defaults
 *   to the wall clock. Injected so suites can freeze time, and so a future
 *   "as of <date>" view parameter has one place to bind.
 * @returns {*}  Substituted value, or the original when no token matched.
 */
export function resolveFilterValue(value, ctx) {
  if (value === '$today')     return toLocalDayString(nowFrom(ctx));
  if (value === '$now')       return nowFrom(ctx).toISOString();
  if (value === '$context')   return ctx?.contextItemId ?? null;
  if (value === VIEWER_TOKEN) return ctx?.currentUserId ?? null;

  const treeType = parseContextTreeToken(value);
  if (treeType !== null) {
    const root = ctx?.contextItemId;
    return root ? subtreeIds(root, ctx?.allEdges, treeType).join(',') : null;
  }

  const answerKey = parseMyAnswerToken(value);
  if (answerKey !== null) {
    // Absent slice and unanswered question are the same answer: nothing. The caller decides what
    // to load (see profileAnswerFieldsForCollection); a filter that names a field nobody fetched
    // must narrow to nothing rather than silently drop out of the query.
    const answer = ctx?.viewerAnswers?.[answerKey];
    return answer == null ? null : answer;
  }

  const offset = parseDayOffsetToken(value);
  if (offset !== null) return toLocalDayString(shiftLocalDays(nowFrom(ctx), offset));

  return value;
}

// $today+7d / $today-2w → a signed day count, or null when `value` isn't an
// offset token. Plain `$today` is NOT a match (it's handled above, and callers
// treat it as its own mode) — only the explicitly offset forms. Weeks
// normalize to days, so a hand-authored "$today-2w" reads back as 14 days;
// makeDayOffsetToken only ever emits days, which is the round-trip the UI uses.
const DAY_OFFSET_TOKEN_RE = /^\$today([+-])(\d{1,4})([dw])$/;

export function parseDayOffsetToken(value) {
  if (typeof value !== 'string') return null;
  const m = DAY_OFFSET_TOKEN_RE.exec(value);
  if (!m) return null;
  const magnitude = Number(m[2]) * (m[3] === 'w' ? 7 : 1);
  return m[1] === '-' ? -magnitude : magnitude;
}

/**
 * Build a day-offset token. `unit` is 'd' or 'w'; a count of 0 collapses to
 * plain `$today` so there's one canonical spelling for "today".
 *
 * @param {number} count   Whole units, may be negative for the past.
 * @param {'d'|'w'} [unit]
 * @returns {string}
 */
export function makeDayOffsetToken(count, unit = 'd') {
  const n = Math.trunc(count);
  if (n === 0) return '$today';
  return `$today${n > 0 ? '+' : '-'}${Math.abs(n)}${unit}`;
}

/** True for any token this module substitutes into a comparison value. */
export function isRelativeDateToken(value) {
  return value === '$today' || value === '$now' || parseDayOffsetToken(value) !== null;
}

// ─── Durations (the is_within_* value shape) ──────────────────────────────

// "48h" / "7d" / "2w". Same unit letters as the offset tokens, so there's one
// vocabulary to learn. Months are deliberately absent: "within the next month"
// has no single correct length, and weeks cover the range people actually want.
const DURATION_RE = /^(\d{1,5})(h|d|w)$/;

/**
 * Parse a duration value into { count, unit }, or null when unparseable.
 * A zero count is rejected — an empty window would match nothing, which reads
 * as a broken filter rather than as a deliberate one.
 */
export function parseDurationValue(value) {
  if (typeof value !== 'string') return null;
  const m = DURATION_RE.exec(value.trim());
  if (!m) return null;
  const count = Number(m[1]);
  if (count <= 0) return null;
  return { count, unit: m[2] };
}

/**
 * Build a duration value. Clamped to at least 1 so the UI's number input can't
 * produce a window that matches nothing.
 *
 * @param {number} count
 * @param {'h'|'d'|'w'} [unit]
 */
export function makeDurationValue(count, unit = 'd') {
  const n = Math.max(1, Math.trunc(Number(count) || 0));
  return `${n}${unit}`;
}

/**
 * Offset an instant by a duration, preserving the time of day.
 *
 * Hours use millisecond arithmetic (an hour is an hour). Days and weeks use
 * calendar arithmetic, so a window spanning a DST change stays a whole number
 * of days. Distinct from shiftLocalDays above, which drops the clock because it
 * feeds a YYYY-MM-DD token.
 */
function addDuration(from, count, unit) {
  if (unit === 'h') return new Date(from.getTime() + count * 3_600_000);
  const days = unit === 'w' ? count * 7 : count;
  return new Date(
    from.getFullYear(), from.getMonth(), from.getDate() + days,
    from.getHours(), from.getMinutes(), from.getSeconds(), from.getMilliseconds(),
  );
}

// Whole-day arithmetic on calendar components rather than ±N × 86_400_000 ms:
// across a DST boundary the millisecond form lands 23 or 25 hours away and can
// resolve to the wrong calendar day.
function shiftLocalDays(d, delta) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + delta);
}

/**
 * The instant relative-date comparisons resolve against. Accepts a Date, an ISO
 * string, or epoch ms on ctx.now; falls back to the wall clock.
 */
function nowFrom(ctx) {
  const n = ctx?.now;
  if (n instanceof Date) return isNaN(n.getTime()) ? new Date() : n;
  if (typeof n === 'string' || typeof n === 'number') {
    const d = new Date(n);
    if (!isNaN(d.getTime())) return d;
  }
  return new Date();
}

// YYYY-MM-DD in the local zone. The year is zero-padded to 4 digits to stay
// parseable by DAY_ONLY_RE and by <input type="date"> (see utils/datetimeLocal.js).
function toLocalDayString(d) {
  const y  = String(d.getFullYear()).padStart(4, '0');
  const m  = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

// ─── Field reads ──────────────────────────────────────────────────────────

/**
 * Read a field value from a node, regardless of which bucket it lives in.
 *
 * Precedence — matches the rest of the codebase (fieldMap.js:417,
 * mergeConnections.js, readNodeFlexDate in timeGridUtils.js, readField in
 * jsonExport.js): customFields wins, then _rawFields, then a camelCase
 * top-level node property fallback for legacy graph-state shapes.
 *
 * The optional `opts.source` hint ('custom' | 'internal') lets the future
 * chip-builder skip the fallback when it already knows the field's origin.
 * Today's resolver callers pass nothing and get try-both.
 */
export function readNodeField(node, fieldName, opts = {}) {
  if (!node || !fieldName) return undefined;

  // Created is COMPUTED, not stored — the source date when the item was imported, else row
  // birth (utils/createdDate.js). It belongs here for the same reason promoted columns do:
  // this is the one reader that knows a value need not sit in customFields. Ahead of the
  // source hints deliberately, since neither bucket will ever hold it.
  if (isCreatedField(fieldName)) return createdDate(node);

  // Is Collection is DERIVED too, and for the same reason belongs ahead of the source
  // hints: neither bucket holds it. `items.is_collection` is a generated column that
  // itemRowToRecord never carries onto the record — it synthesizes the self-referential
  // `Collection` link instead — so the bucket reads below would return undefined and
  // every "show me only collections" filter would match nothing at all.
  // See utils/collectionField.js.
  if (isCollectionField(fieldName)) return collectionFlag(node);
  // Price is a band derived from the price fields, in neither bucket either (utils/priceBandField.js).
  if (isPriceBandField(fieldName)) return priceBandWord(node);
  // Approval rides on node.approval, in neither bucket (utils/approvalField.js).
  if (isApprovalField(fieldName)) return approvalWord(node);
  // Space is the owning space's name, from `node.spaceId` (data/owningSpace.js).
  if (isSpaceField(fieldName)) return owningSpaceName(node);

  if (opts.source === 'custom') {
    return node.customFields?.[fieldName];
  }
  if (opts.source === 'internal') {
    return node._rawFields?.[fieldName] ?? node[toCamelCase(fieldName)];
  }

  // No source hint — try-both, customFields first.
  const fromCustom = node.customFields?.[fieldName];
  if (fromCustom !== undefined) return fromCustom;
  const fromRaw = node._rawFields?.[fieldName];
  if (fromRaw !== undefined) return fromRaw;
  return node[toCamelCase(fieldName)];
}

function toCamelCase(s) {
  return s.toLowerCase().replace(/\s+(.)/g, (_, c) => c.toUpperCase());
}

// ─── Date coercion (precision-aware) ─────────────────────────────────────
//
// A date comparison has two sides, and each side carries a precision: a bare
// calendar day ("2026-08-11", a `date` field, $today) or an instant
// ("2026-08-11T17:00:00Z", a `dateTime` field, $now). Comparing them naively —
// which is what the Grid engine did, `new Date(raw)` against
// `new Date(filterVal)` — gets two things wrong:
//
//   1. Day bounds excluded their own day. "is on or before Aug 11" against a
//      value stamped Aug 11 09:00 compared 09:00 <= 00:00 and rejected it, so
//      the operator quietly excluded almost all of the day it names.
//   2. The two sides used different anchors. Per spec, `new Date('2026-08-11')`
//      is UTC midnight while `new Date('2026-08-11T09:00:00')` (no offset) is
//      LOCAL — so a day bound met an instant across a timezone shift, and for a
//      UTC-7 viewer "is before Aug 11" actually meant "before Aug 10, 17:00".
//
// Both fall out of one rule: parse day-only values at LOCAL midnight, tag each
// side with its precision, and compare at the COARSER of the two. Coarsening is
// the whole semantic — it is what makes "expires Aug 11" stay live all day on
// Aug 11 while "expires Aug 11, 5:00 PM" expires at 5:01 PM, out of a single
// operator and a single code path.

// ANNUAL DATES. A FlexDate with `year: null` is a recurring day — a birthday,
// an anniversary. It has no year, so "is it before March?" has no single answer,
// and the previous behavior was to return null and match nothing at all: every
// recurring date silently vanished from every date filter.
//
// The policy, which differs by question because the questions differ:
//
//   - Point and bound comparisons (is_on, is_before, is_in_the_past, …) compare
//     the occurrence in the CURRENT year. "Whose birthday have we passed?" is
//     then a well-defined question with a useful answer.
//   - Window comparisons (is_within_next / is_within_past) match if ANY yearly
//     occurrence lands in the window. This is what recurrence means, and it's
//     the difference between a working "birthdays in the next 30 days" chip and
//     one that drops every January birthday for the whole of December.
//
// Other partial precisions (year-only, year-month) keep flexDate's documented
// convention of defaulting to the start of the unit, so { year: 2026 } compares
// as 2026-01-01.

// A bare calendar date, no time component. Anchored locally, not UTC.
const DAY_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** A recurring FlexDate — { year: null, month, day }, validated. */
function isAnnualValue(v) {
  return v != null
    && typeof v === 'object'
    && !Array.isArray(v)
    && v.year === null
    && isAnnual(v);
}

/**
 * The occurrences of an annual date in the years bracketing `now`. Three is
 * enough for any window the duration grammar can express (max 99999 weeks would
 * need more, but a window that wide matches every occurrence anyway).
 */
function annualStampsAround(v, now) {
  const y = now.getFullYear();
  const out = [];
  for (const year of [y - 1, y, y + 1]) {
    const d = flexDateToDate(projectToYear(v, year));
    if (d !== null && !isNaN(d.getTime())) out.push({ t: d, precision: 'day' });
  }
  return out;
}

/**
 * Apply the field-type precision override, then tag the stamp.
 *
 * An Airtable `date` field can arrive carrying a stray time component;
 * formatDateValue() already strips it for display (see utils/formatDate.js),
 * and comparison has to agree — otherwise "expires Aug 11" reads as expired at
 * 00:01 on Aug 11 for exactly the records whose wire value happens to have a
 * clock on it.
 */
function stamp(t, precisionTier, opts) {
  if (opts?.forceDay && precisionTier === 'minute') {
    return { t: startOfDay(t), precision: 'day' };
  }
  return { t, precision: precisionTier };
}

/**
 * Convert a value to { t: Date, precision: 'day' | 'minute' } for comparison.
 * Accepts:
 *   - Date instances (instant → 'minute')
 *   - Day-only strings "2026-06-09" (→ 'day', parsed at LOCAL midnight)
 *   - ISO datetime strings "2026-06-09T18:00:00Z" (→ 'minute')
 *   - FlexDate objects ({ year, month, day, hour?, minute? }) — the in-memory
 *     shape the Calendar view uses; defensive in case a customField value
 *     hasn't been serialized to its wire form yet. Precision follows whether
 *     an `hour` key is present, matching flexDate.js's own precision().
 *
 * Returns null on null/empty/invalid.
 *
 * @param {*} v
 * @param {{ forceDay?: boolean, now?: Date }} [opts]  forceDay coarsens an
 *   instant to its local day — set when the field's declared type is
 *   day-precision. `now` supplies the year an annual date projects onto.
 */
function coerceToStamp(v, opts) {
  if (v == null || v === '') return null;

  if (v instanceof Date) {
    return isNaN(v.getTime()) ? null : stamp(v, 'minute', opts);
  }

  // Annual (recurring) — project onto the current year. See the ANNUAL note above.
  if (isAnnualValue(v)) {
    const year = (opts?.now instanceof Date ? opts.now : new Date()).getFullYear();
    const d = flexDateToDate(projectToYear(v, year));
    return d === null ? null : stamp(d, 'day', opts);
  }

  if (typeof v === 'object' && Number.isInteger(v.year)) {
    const d = new Date(
      v.year,
      (v.month || 1) - 1,
      v.day || 1,
      v.hour || 0,
      v.minute || 0,
    );
    if (isNaN(d.getTime())) return null;
    return stamp(d, v.hour !== undefined ? 'minute' : 'day', opts);
  }

  if (typeof v === 'string') {
    const m = DAY_ONLY_RE.exec(v.trim());
    if (m) {
      const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      return isNaN(d.getTime()) ? null : stamp(d, 'day', opts);
    }
  }

  const d = new Date(v);
  return isNaN(d.getTime()) ? null : stamp(d, 'minute', opts);
}

/**
 * Comparator over two stamps, coarsening to the less precise of the two.
 * Negative / zero / positive, like any sort comparator.
 */
function compareStamps(a, b) {
  if (a.precision === 'day' || b.precision === 'day') {
    return startOfDay(a.t).getTime() - startOfDay(b.t).getTime();
  }
  return a.t.getTime() - b.t.getTime();
}

// ─── Comparator ───────────────────────────────────────────────────────────

/**
 * Apply a single predicate. Already-extracted raw value in, boolean out.
 *
 * @param {*} raw    The value pulled from the node (via readNodeField).
 * @param {string} op    Operator name. Aliases resolved internally.
 * @param {*} target The comparison target (already $today/$now-substituted).
 * @param {string} [fieldType] Optional hint. Number / date ops use it; text
 *   ops are type-agnostic. Resolver callers today pass undefined; Grid calls
 *   pass `col.type` when available. For dates it decides whether an instant is
 *   coarsened to its day — 'date' is day-precision, 'dateTime' /
 *   'createdTime' / 'lastModifiedTime' are real timestamps.
 * @param {Object} [ctx]  Carries ctx.now for the relative-to-now ops
 *   (is_in_the_past / is_in_the_future). Defaults to the wall clock.
 * @returns {boolean}
 */
export function applyPredicate(raw, op, target, fieldType, ctx) {
  const canonical = canonicalizeOp(op);

  // Empty / checkbox — no target needed.
  switch (canonical) {
    case 'is_empty':
      return raw == null || raw === '' || (Array.isArray(raw) && raw.length === 0);
    case 'is_not_empty':
      return raw != null && raw !== '' && !(Array.isArray(raw) && raw.length === 0);
    case 'is_checked':
      return !!raw;
    case 'is_not_checked':
      return !raw;
    default:
      break;
  }

  switch (canonical) {
    case '=':
      // Existing resolver behavior: case-sensitive String() equality. Pinned
      // by tests; intentionally distinct from `is` (case-insensitive).
      return String(raw ?? '') === String(target ?? '');
    case '!=':
      return String(raw ?? '') !== String(target ?? '');

    case 'is':
      // Grid's case-insensitive equality. Resolver gains this op via the
      // shared comparator — new in this round.
      return String(raw ?? '').toLowerCase() === String(target ?? '').toLowerCase();
    case 'is_not':
      return String(raw ?? '').toLowerCase() !== String(target ?? '').toLowerCase();

    case '>':
    case 'gt':
      return Number(raw) > Number(target);
    case '<':
    case 'lt':
      return Number(raw) < Number(target);
    case '>=':
    case 'gte':
      return Number(raw) >= Number(target);
    case '<=':
    case 'lte':
      return Number(raw) <= Number(target);

    case 'contains':
      return String(raw ?? '').toLowerCase().includes(String(target ?? '').toLowerCase());
    case 'does_not_contain':
      return !String(raw ?? '').toLowerCase().includes(String(target ?? '').toLowerCase());

    case 'is_on':
    case 'is_before':
    case 'is_after':
    case 'is_on_or_before':
    case 'is_on_or_after': {
      const now = nowFrom(ctx);
      const a = coerceToStamp(raw, { forceDay: fieldType === 'date', now });
      const b = coerceToStamp(target, { now });
      if (a === null || b === null) return false;
      const cmp = compareStamps(a, b);
      if (canonical === 'is_on')           return cmp === 0;
      if (canonical === 'is_before')       return cmp < 0;
      if (canonical === 'is_after')        return cmp > 0;
      if (canonical === 'is_on_or_before') return cmp <= 0;
      return cmp >= 0;   // is_on_or_after
    }

    // Relative to now — no target. Precision coarsening does the semantic work:
    // a day-precision value is "in the past" only once the whole day is behind
    // us, while a timestamped one flips the moment it passes. That leaves today
    // in neither bucket for day-precision fields, which is the correct reading
    // of an expiration date; "not expired yet" is is_on_or_after + $today.
    case 'is_in_the_past':
    case 'is_in_the_future': {
      const now = nowFrom(ctx);
      const a = coerceToStamp(raw, { forceDay: fieldType === 'date', now });
      if (a === null) return false;
      const cmp = compareStamps(a, { t: now, precision: 'minute' });
      return canonical === 'is_in_the_past' ? cmp < 0 : cmp > 0;
    }

    // Windows. The target is a duration ("7d"), and the bounds are inclusive on
    // both ends: [now, now + d] looking forward, [now - d, now] looking back.
    // Coarsening still applies, so on a day-precision field the window covers
    // whole days at each end, while on a timestamped field "48h" means exactly
    // 48 hours.
    case 'is_within_next':
    case 'is_within_past': {
      const duration = parseDurationValue(target);
      if (duration === null) return false;

      const now = nowFrom(ctx);
      const forward = canonical === 'is_within_next';
      // The far edge is behind `now` for a backward window, so the offset is
      // signed. Getting this wrong inverts the bounds (lo > hi) and the window
      // matches nothing at all rather than failing loudly.
      const edge = addDuration(now, forward ? duration.count : -duration.count, duration.unit);
      const [lo, hi] = forward ? [now, edge] : [edge, now];
      const loStamp = { t: lo, precision: 'minute' };
      const hiStamp = { t: hi, precision: 'minute' };

      // A recurring date matches when ANY of its occurrences lands in the
      // window — see the ANNUAL note above.
      const candidates = isAnnualValue(raw)
        ? annualStampsAround(raw, now)
        : [coerceToStamp(raw, { forceDay: fieldType === 'date', now })].filter((s) => s !== null);

      return candidates.some((a) =>
        compareStamps(a, loStamp) >= 0 && compareStamps(a, hiStamp) <= 0);
    }

    case 'has_any':
    case 'has_none': {
      // Generic id/scalar membership. For column-aware shapes (displayType /
      // multipleRecordLinks where the column's render label differs from the
      // stored id), Grid's testFilter still runs its own id-vs-label dispatch
      // before delegating here — see GridCanvas/filters.js. Resolver callers
      // get scalar/id semantics: extract `id` from object array items.
      const arr = Array.isArray(raw) ? raw : (raw == null || raw === '' ? [] : [raw]);
      const ids = arr.map((v) => (v && typeof v === 'object' ? v.id ?? String(v) : String(v))).filter(Boolean);
      const targets = String(target ?? '').split(',').map((s) => s.trim()).filter(Boolean);
      if (targets.length === 0) return canonical === 'has_none'; // empty filter = match nothing for has_any, everything for has_none
      const hasMatch = targets.some((t) => ids.includes(t));
      return canonical === 'has_any' ? hasMatch : !hasMatch;
    }

    default:
      // Unknown operator — preserve the resolver's historical "fail open"
      // behavior so a stored query with a stray op name doesn't nuke an
      // entire view. The chip-builder UI is the actual gate for which ops
      // get used.
      return true;
  }
}

// ─── Cell predicates (Phase 3 — cells as queryable features) ────────────────
//
// A cell filter tests an *interaction cell* — the (author, subject, field) value
// the cell substrate stores ({ value_text, value_numeric, value_json }) — instead
// of a node field. It's how "items I favorited" / "watched >50%" / "reacted-to"
// become normal queries. The op family selects which value column is read:
//
//   presence ops  (is_checked / is_not_checked / is_empty / is_not_empty)
//                 → test the cell's existence + truthiness (favorited vs not).
//   number ops    (> < >= <= / gt lt gte lte)  → read value_numeric (fractions).
//   text  ops     (is / contains / = / …)      → read value_text (reaction kind).
//
// `cell` is the matched row for (viewer, subject, field), or null/undefined when
// the viewer has no such cell on that subject.

const CELL_NUMBER_OPS = new Set(['>', '<', '>=', '<=', 'gt', 'lt', 'gte', 'lte']);

// A cell counts as "true"/checked when it stores an affirmative value. Covers
// bookmark (value_text='true'), reaction (value_text='like'), numeric flags, and
// a bare value-less cell (presence alone = flagged).
function cellIsTrue(cell) {
  if (cell == null) return false;
  if (cell.value_text != null) {
    const t = String(cell.value_text).toLowerCase();
    return t === 'true' || t === '1' || t === 'yes' || t === 'like';
  }
  if (cell.value_numeric != null) return Number(cell.value_numeric) > 0;
  return true;
}

function pickCellValue(cell, canonical) {
  if (cell == null) return undefined;
  if (CELL_NUMBER_OPS.has(canonical)) return cell.value_numeric;
  return cell.value_text ?? cell.value_numeric ?? null;
}

/**
 * Apply a predicate against a viewer's interaction cell. Cell row (or null) in,
 * boolean out. Reuses the operator vocabulary + comparator above so cell queries
 * and field queries speak one language.
 *
 * @param {Object|null|undefined} cell  The (viewer, subject, field) cell row, or null.
 * @param {string} op       Operator name (aliases resolved internally).
 * @param {*} target        Comparison target (already token-substituted).
 * @returns {boolean}
 */
export function applyCellPredicate(cell, op, target) {
  switch (canonicalizeOp(op)) {
    case 'is_checked':     return cellIsTrue(cell);
    case 'is_not_checked': return !cellIsTrue(cell);
    case 'is_empty':       return cell == null;   // no cell for this viewer/subject
    case 'is_not_empty':   return cell != null;   // a cell exists
    default:               break;
  }
  if (cell == null) return false;                 // value comparisons require a cell
  return applyPredicate(pickCellValue(cell, canonicalizeOp(op)), op, target);
}
